// AGC: WhatsApp-style inline recording bar — replaces BottomNav during recording
import React from 'react'
import { motion } from 'framer-motion'
import { Mic, Trash2, Send, Pause, Play, ChevronDown } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { AudioWaveform } from './AudioWaveform'

interface VoiceRecordingBarProps {
  mode: 'tap' | 'hold'
  duration: number
  isRecording: boolean
  isPaused: boolean
  analyserNode: AnalyserNode | null
  onSend: () => void
  onCancel: () => void
  onPause: () => void
  onResume: () => void
  onSlideCancel: () => void
  onRelease: () => void
}

// AGC: Format seconds as M:SS
const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const VoiceRecordingBar: React.FC<VoiceRecordingBarProps> = ({
  mode,
  duration,
  isRecording,
  isPaused,
  analyserNode,
  onSend,
  onCancel,
  onPause,
  onResume,
}) => {
  const { t } = useTranslation()

  // AGC: Tap mode — two-row layout with timer/waveform and controls
  if (mode === 'tap') {
    return (
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-[90]"
      >
        {/* AGC: pb uses safe-area-inset-bottom to cover the Android nav bar fully */}
        <div
          className="bg-[#1a1a1a] rounded-t-2xl px-4 pt-4 space-y-3"
          style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 0px) + 12px)' }}
        >
          {/* AGC: Top row — timer + waveform */}
          <div className="flex items-center gap-3">
            <span className="text-white font-mono text-base min-w-[3rem]">
              {formatDuration(duration)}
            </span>
            <AudioWaveform
              analyserNode={analyserNode}
              isActive={isRecording && !isPaused}
            />
          </div>

          {/* AGC: Bottom row — delete / pause-resume / send */}
          <div className="flex items-center justify-between px-2">
            <button
              onClick={onCancel}
              className="p-3 text-gray-400 hover:text-white transition-colors"
              aria-label={t('voice.recordingBar.delete')}
            >
              <Trash2 className="w-6 h-6" />
            </button>

            <button
              onClick={isPaused ? onResume : onPause}
              className="p-3 transition-colors"
              aria-label={isPaused ? t('voice.recordingBar.resume') : t('voice.recordingBar.pause')}
            >
              {isPaused
                ? <Play className="w-8 h-8 text-green-500 hover:text-green-400" />
                : <Pause className="w-8 h-8 text-red-500 hover:text-red-400" />
              }
            </button>

            <button
              onClick={onSend}
              className="w-12 h-12 bg-[#22c55e] rounded-full flex items-center justify-center hover:bg-[#16a34a] transition-colors"
              aria-label={t('voice.recordingBar.send')}
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </motion.div>
    )
  }

  // AGC: Hold mode — recording info above the bottom bar, with a gap around the mic button.
  // AGC: Uses two dark panels (left + right) around a center gap for the mic button.
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed bottom-0 left-0 right-0 z-[82] pointer-events-none"
    >
      {/* AGC: Recording info panel — well above the mic button so user can read it
          while their finger/thumb covers the mic. Extra pb-8 gives clearance. */}
      <div className="bg-[#1a1a1a] mx-0 rounded-t-2xl px-4 pt-3 pb-20 space-y-2">
        <div className="flex items-center gap-3">
          <Mic className="w-4 h-4 text-red-500 animate-pulse flex-shrink-0" />
          <span className="text-white font-mono text-base min-w-[3rem]">
            {formatDuration(duration)}
          </span>
          <AudioWaveform
            analyserNode={analyserNode}
            isActive={isRecording}
          />
        </div>
        <div className="flex items-center justify-center gap-1">
          <ChevronDown className="w-3 h-3 text-gray-400" />
          <span className="text-gray-400 text-xs">
            {t('voice.recordingBar.slideToCancel')}
          </span>
        </div>
      </div>

      {/* AGC: Bottom bar — two panels with a gap in the center for the mic button */}
      <div
        className="flex"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)', backgroundColor: '#1a1a1a' }}
      >
        <div className="flex-1 bg-[#1a1a1a] h-[52px]" />
        {/* AGC: Center gap — 72px wide to clear the 56px mic button + padding */}
        <div className="w-[72px] bg-transparent" style={{ borderRadius: '0 0 50% 50% / 0 0 100% 100%', backgroundColor: 'transparent' }} />
        <div className="flex-1 bg-[#1a1a1a] h-[52px]" />
      </div>
    </motion.div>
  )
}

export default VoiceRecordingBar
